import { isNil } from 'lodash-es';

import { AccountingSoftwareBillAttachmentDetails } from '@/features/accounting-software';
import { Instance as companyAccountingSoftwareService } from '@/features/accounting-software/services/companyAccountingSoftware';
import DocumentService from '@/legacy/features/companies/documents/services/document';
import * as paymentService from '@/legacy/services/company/payment';

import { Instance as companyFileStorageService } from '../services/companyFileStorageService';
import { Instance as fileStorageService } from '../services/fileStorageService';
import { FileAttachmentCallbacks } from '../types/FileAttachmentCallbacks';
import { FileUploadStatus } from '../types/FileUploadStatus';
import { UploadedFileViewModel } from '../types/UploadedFileViewModel';

/**
 * Keep track of how many attachments are in progress of uploading.
 * We can use this to ensure user can't continue if files are still being uploaded for a new payment.
 **/
let filesUploadingCount = 0;

export const hasFilesUploading = () => filesUploadingCount > 0;

/**
 * Creates all the callbacks in one place to avoid adding these functions as methods to payments/addMultiple.ts
 **/
export const createBillAttachmentCallbacks = (
  newPayment: paymentService.NewPayment
): FileAttachmentCallbacks => {
  /**
   * This will be called in react FileAttachmentsFragment when a bill was selected with attachments in accounting software.
   * Also groups all of the logic for attachments and uploads in a single place
   */
  const getAttachments = () =>
    companyAccountingSoftwareService.getAttachmentsForBillId(newPayment.billId);

  /**
   * This will be called in react FileAttachmentsFragment when clicking on an accounting software bill
   */
  const viewAttachment = (attachment: AccountingSoftwareBillAttachmentDetails) => {
    const accountingSoftware = companyAccountingSoftwareService.softwarePackage;
    if (isNil(accountingSoftware)) {
      return;
    }

    accountingSoftware.GetBillAttachment(attachment);
  };

  /**
   * This will be called in react FileAttachmentsFragment when clicking on an accounting software bill published from Document Tab
   */
  const viewOcrAttachment = async (documentFileId: string) => {
    const link = await DocumentService.createDocumentPreviewLink(documentFileId);
    window.open(link);
  };

  /**
   * This will be called in react FileAttachmentsFragment when clicking on file attachment viewing
   */
  const viewUploadedFile = async (uploadedFile: UploadedFileViewModel) => {
    const fileStorageDownloadLinkViewModel =
      await companyFileStorageService.generateDownloadFileAttachmentLink(
        uploadedFile.companyFileStorageFileId
      );
    await fileStorageService.viewFileStorageFile(fileStorageDownloadLinkViewModel);
  };

  /**
   * This will be called in react FileAttachmentsFragment when clicking on the delete button for an payable attachment
   */
  const fileDeleted = async (uploadedFile: UploadedFileViewModel) =>
    fileStorageService.delete(uploadedFile.companyFileStorageFileId);

  /**
   * This is called in react FileAttachmentsFragment when user selected some files to upload
   */
  const filesSelected = async (fileList: FileList) =>
    // If this throws the FileAttachmentsFragment.tsx QueryFilesSelected will catch and show error modal
    fileStorageService.upload(fileList);

  const filesUploaded = async (uploadedFiles: Array<UploadedFileViewModel>) => {
    newPayment.uploadedFiles = uploadedFiles;
  };

  const fileUploadStatusChanged = (newFileUploadStatus: FileUploadStatus) => {
    filesUploadingCount += newFileUploadStatus === FileUploadStatus.Uploading ? 1 : -1;
  };

  return {
    getAttachments,
    viewAttachment,
    viewOcrAttachment,

    fileDeleted,
    filesSelected,
    viewUploadedFile,
    filesUploaded,

    fileUploadStatusChanged,
  };
};
